import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ArrowLeft, ArrowRight, CheckCircle, UploadSimple, Trash, File as FileIcon, CheckCircle as DoneIcon, Warning } from "@phosphor-icons/react";
import TagsInput from "@/components/TagsInput";
import RequiredMark from "@/components/RequiredMark";

const SCALE = [
    { value: 1, label: "Strongly disagree" },
    { value: 2, label: "Disagree" },
    { value: 3, label: "Neutral" },
    { value: 4, label: "Agree" },
    { value: 5, label: "Strongly agree" },
];

const AGREEMENTS = [
    "Enterprise Agreement (EA)",
    "Microsoft Customer Agreement (MCA-E)",
    "Cloud Solution Provider (CSP)",
    "Open / Open Value",
    "Mixed / Multiple",
    "Don't know / Need to confirm",
];

const INDUSTRIES = [
    "Financial Services",
    "Public Sector",
    "Healthcare",
    "Manufacturing",
    "Professional Services",
    "Retail & Consumer",
    "Technology",
    "Education",
    "Energy & Utilities",
    "Non-profit",
    "Other",
];

const ORG_TYPES = [
    { v: "private", l: "Private sector company" },
    { v: "public", l: "Public sector / government" },
    { v: "non-profit", l: "Non-profit / NGO" },
    { v: "education", l: "Education institution" },
];
const ENGAGEMENT_GOALS = [
    { v: "renewal", l: "EA / agreement renewal preparation" },
    { v: "optimisation", l: "Cost-and-risk optimisation" },
    { v: "compliance", l: "Compliance & audit readiness" },
    { v: "ma", l: "M&A / restructuring due diligence" },
];
const EA_LIFECYCLE = [
    { v: "just-signed", l: "Just signed (0–1 year in)" },
    { v: "mid", l: "Mid-term (year 2)" },
    { v: "approaching", l: "Approaching renewal (year 3)" },
    { v: "expired", l: "Expired" },
    { v: "rolling", l: "Rolling / month-to-month" },
];
const PRODUCT_OPTIONS = [
    "Microsoft 365 (E1/E3/E5/F-series)",
    "Office 365 (E1/E3/E5)",
    "Microsoft 365 Copilot",
    "Dynamics 365",
    "Power Platform (BI / Apps / Automate / Pages)",
    "Defender / Purview / Entra",
    "Project / Visio",
    "Windows / Windows Server",
    "SQL Server",
    "Azure",
    "Teams Phone / Calling Plans",
];
const PAIN_OPTIONS = [
    "Overpaying for unused licences",
    "Non-compliant (under-licensed)",
    "No visibility / poor tracking",
    "Inflated tier — wrong SKUs assigned",
    "Renewal approaching with weak data",
    "Copilot rollout under consideration",
    "Mergers / restructuring in progress",
];

export default function Assessment() {
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const editId = searchParams.get("edit");
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");
    const [questions, setQuestions] = useState([]);
    const [categories, setCategories] = useState({});
    const [categoryOrder, setCategoryOrder] = useState([]);
    const [step, setStep] = useState(0); // 0 = context, then one per category, then review
    const [answers, setAnswers] = useState({}); // {qid: value}
    const [context, setContext] = useState({
        organization_name: "",
        industry: "",
        user_count: "",
        agreement_type: "",
        license_types: "",
        annual_spend: "",
        organisation_type: "",
        engagement_goal: "",
        ea_lifecycle_stage: "",
        products_in_scope: [],
        pain_points: [],
        country: "",
        data_protection_regime: "",
        security_tools: [],
    });
    const [evidence, setEvidence] = useState([]); // local list of { file, status, id?, chars?, error? }
    const [evidenceUploading, setEvidenceUploading] = useState(false);
    const [editDraft, setEditDraft] = useState(null); // {id, version_number, organization_name, ...} when editing
    const [inheritedEvidence, setInheritedEvidence] = useState([]); // server-side rows carried from parent
    const [removingInheritedId, setRemovingInheritedId] = useState(null);
    const [setupLists, setSetupLists] = useState({ countries: [], security_tools: [] });

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/questions");
                setQuestions(data.questions);
                setCategories(data.categories);
                setCategoryOrder(data.category_order);
                try {
                    const { data: lists } = await api.get("/config/setup-lists");
                    setSetupLists(lists || { countries: [], security_tools: [] });
                } catch (e) {
                    console.debug("setup-lists fetch failed (non-fatal):", e?.message || e);
                }
                if (editId) {
                    const { data: draft } = await api.get(`/assessments/${editId}`);
                    setEditDraft(draft);
                    setContext({
                        organization_name: draft.organization_name || "",
                        industry: draft.industry || "",
                        user_count: String(draft.user_count ?? ""),
                        agreement_type: draft.agreement_type || "",
                        license_types: draft.license_types || "",
                        annual_spend: String(draft.annual_spend ?? ""),
                        organisation_type: draft.organisation_type || "",
                        engagement_goal: draft.engagement_goal || "",
                        ea_lifecycle_stage: draft.ea_lifecycle_stage || "",
                        products_in_scope: draft.products_in_scope || [],
                        pain_points: draft.pain_points || [],
                        country: draft.country || "",
                        data_protection_regime: draft.data_protection_regime || "",
                        security_tools: draft.security_tools || [],
                    });
                    const ans = {};
                    for (const a of draft.answers || []) ans[a.question_id] = a.value;
                    setAnswers(ans);
                    // Fetch inherited evidence so we can show "carried over from previous version"
                    try {
                        const { data: ev } = await api.get(`/assessments/${editId}/evidence`);
                        setInheritedEvidence(ev || []);
                    } catch (err) {
                        console.debug("inherited evidence fetch failed (non-fatal):", err?.message || err);
                    }
                }
            } catch (e) {
                setErr(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editId]);

    const grouped = useMemo(() => {
        const g = {};
        for (const cat of categoryOrder) g[cat] = [];
        questions.forEach((q) => {
            (g[q.category] = g[q.category] || []).push(q);
        });
        for (const c of Object.keys(g))
            g[c].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        return g;
    }, [questions, categoryOrder]);

    const totalSteps = 1 + categoryOrder.length + 2; // context + categories + evidence + review
    const isContext = step === 0;
    const isReview = step === totalSteps - 1;
    const isEvidence = step === totalSteps - 2;
    const currentCategory =
        step >= 1 && step <= categoryOrder.length ? categoryOrder[step - 1] : null;

    const contextValid = useMemo(() => {
        return (
            context.organization_name.trim() &&
            context.industry &&
            String(context.user_count).trim() &&
            Number(context.user_count) > 0 &&
            context.agreement_type &&
            context.license_types.trim()
        );
    }, [context]);

    const currentCategoryComplete = useMemo(() => {
        if (!currentCategory) return true;
        const qs = grouped[currentCategory] || [];
        return qs.every((q) => answers[q.id] != null);
    }, [grouped, currentCategory, answers]);

    const allAnswered = useMemo(() => {
        return questions.length > 0 && questions.every((q) => answers[q.id] != null);
    }, [questions, answers]);

    const setAnswer = (qid, value) =>
        setAnswers((prev) => ({ ...prev, [qid]: value }));

    const uploadEvidence = async (assessmentId, file, documentType = "other") => {
        const buf = await file.arrayBuffer();
        const url = `/assessments/${assessmentId}/evidence?filename=${encodeURIComponent(file.name)}&document_type=${encodeURIComponent(documentType)}`;
        const { data } = await api.post(url, buf, {
            headers: { "Content-Type": "application/octet-stream" },
            transformRequest: [(d) => d],
        });
        return data;
    };

    const onPickFiles = (files, documentType = "other") => {
        const arr = Array.from(files || []);
        const room = 8 - evidence.length;
        const accepted = arr.slice(0, room).map((f) => ({
            file: f,
            documentType,
            status: "pending",
            id: null,
            chars: 0,
            error: null,
        }));
        setEvidence((prev) => [...prev, ...accepted]);
    };
    const removeEvidence = (idx) => {
        setEvidence((prev) => prev.filter((_, i) => i !== idx));
    };

    const removeInheritedEvidence = async (ie) => {
        if (!editId) return;
        const ok = window.confirm(
            `Remove "${ie.filename}" from this re-run?\n\nThe file from the previous version will no longer be used when the report is generated.`
        );
        if (!ok) return;
        setRemovingInheritedId(ie.id);
        try {
            await api.delete(`/assessments/${editId}/evidence/${ie.id}`);
            setInheritedEvidence((prev) => prev.filter((x) => x.id !== ie.id));
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setRemovingInheritedId(null);
        }
    };

    const submit = async () => {
        setSubmitting(true);
        setErr("");
        try {
            const hasNewEvidence = evidence.length > 0;
            const inheritedRows = inheritedEvidence.length;
            const totalEvidence = inheritedRows + hasNewEvidence;
            let aid;

            if (editDraft) {
                // EDIT MODE — patch the draft, upload new evidence, then trigger generation.
                aid = editDraft.id;
                await api.patch(`/assessments/${aid}`, {
                    organization_name: context.organization_name,
                    industry: context.industry,
                    user_count: Number(context.user_count),
                    agreement_type: context.agreement_type,
                    license_types: context.license_types,
                    annual_spend: Number(context.annual_spend),
                    organisation_type: context.organisation_type,
                    engagement_goal: context.engagement_goal,
                    products_in_scope: context.products_in_scope,
                    ea_lifecycle_stage: context.ea_lifecycle_stage,
                    pain_points: context.pain_points,
                    country: context.country,
                    data_protection_regime: context.data_protection_regime,
                    security_tools: context.security_tools,
                    answers: Object.entries(answers).map(([question_id, value]) => ({
                        question_id,
                        value,
                    })),
                });
            } else {
                // NEW MODE — original create flow.
                const payload = {
                    ...context,
                    user_count: Number(context.user_count),
                    annual_spend: Number(context.annual_spend),
                    answers: Object.entries(answers).map(([question_id, value]) => ({
                        question_id,
                        value,
                    })),
                    skip_generation: hasNewEvidence,
                };
                const { data } = await api.post("/assessments", payload);
                aid = data.id;
            }

            if (hasNewEvidence) {
                setEvidenceUploading(true);
                for (let i = 0; i < evidence.length; i++) {
                    const item = evidence[i];
                    setEvidence((prev) =>
                        prev.map((e, k) => (k === i ? { ...e, status: "uploading" } : e))
                    );
                    try {
                        const res = await uploadEvidence(aid, item.file, item.documentType || "other");
                        setEvidence((prev) =>
                            prev.map((e, k) =>
                                k === i
                                    ? { ...e, status: "done", id: res.id, chars: res.chars }
                                    : e
                            )
                        );
                    } catch (e) {
                        const msg = formatApiError(e);
                        setEvidence((prev) =>
                            prev.map((e2, k) =>
                                k === i ? { ...e2, status: "failed", error: msg } : e2
                            )
                        );
                    }
                }
                setEvidenceUploading(false);
            }

            // For edit-mode OR (new-mode with evidence) we must explicitly trigger generation.
            // (New-mode without evidence already ran generation inside POST /assessments.)
            if (editDraft || hasNewEvidence) {
                await api.post(`/assessments/${aid}/generate`);
            }
            void totalEvidence;

            // Navigate immediately to the report page — it renders the branded
            // loading state (logo + spinning ring) and polls for `ready` itself.
            // Previously we polled here for up to 240s while the user stared at
            // a disabled button; the mobile experience of that was awful.
            nav(`/report/${aid}`);
            return;
        } catch (e) {
            // Freemium quota exhausted — hand the user off to the plans page.
            if (e?.response?.status === 402) {
                setErr(
                    formatApiError(e) +
                        " Redirecting you to Plans…"
                );
                setTimeout(() => nav("/plans?from=quota-exhausted"), 1600);
                setSubmitting(false);
                return;
            }
            setErr(formatApiError(e));
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 text-[#c2c2c2] uppercase tracking-widest text-sm">
                Loading questionnaire…
            </div>
        );
    }

    return (
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-12" data-testid="assessment-page">
            {/* Progress */}
            <div className="mb-10">
                <div className="flex items-center justify-between mb-3">
                    <div className="overline">License Risk Scan</div>
                    <div className="text-xs text-[#c2c2c2] tracking-widest uppercase">
                        Step {step + 1} / {totalSteps}
                    </div>
                </div>
                <div className="flex gap-1">
                    {Array.from({ length: totalSteps }).map((_, i) => (
                        <div
                            key={i}
                            className={`flex-1 h-[3px] ${
                                i <= step ? "bg-[#00A896]" : "bg-[#2a2a2a]"
                            }`}
                        />
                    ))}
                </div>
            </div>

            {/* RE-RUN BANNER */}
            {editDraft && (
                <div
                    className="mb-8 border border-[#2ec5b6]/40 bg-[#0f1a19] p-4 flex flex-wrap items-center justify-between gap-3"
                    data-testid="assessment-rerun-banner"
                >
                    <div>
                        <div className="overline mb-1">
                            Re-running v{editDraft.version_number || 2}
                        </div>
                        <div className="text-sm text-[#d4d4d4]">
                            New version for <strong>{editDraft.organization_name}</strong>.
                            Prior answers and evidence are pre-loaded — edit anything,
                            add more evidence, then submit to regenerate.
                        </div>
                    </div>
                    {inheritedEvidence.length > 0 && (
                        <div className="text-[12px] text-[#c2c2c2]">
                            <span className="text-[#2ec5b6] font-mono">
                                {inheritedEvidence.length}
                            </span>{" "}
                            evidence file{inheritedEvidence.length === 1 ? "" : "s"} carried over
                        </div>
                    )}
                </div>
            )}

            {/* CONTEXT */}
            {isContext && (
                <section className="fade-up" data-testid="step-context">
                    <h1 className="font-display text-3xl md:text-5xl leading-tight">
                        Organization context
                    </h1>
                    <p className="text-[#c2c2c2] mt-4 max-w-2xl leading-relaxed">
                        A few details to frame the report. None of this is shared
                        outside the platform.
                    </p>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <label className="label" htmlFor="ctx-org">Organization name<RequiredMark /></label>
                            <input
                                id="ctx-org"
                                className="input-base"
                                value={context.organization_name}
                                onChange={(e) =>
                                    setContext({ ...context, organization_name: e.target.value })
                                }
                                data-testid="ctx-org-input"
                            />
                        </div>
                        <div>
                            <label className="label" htmlFor="ctx-industry">Industry<RequiredMark /></label>
                            <select
                                id="ctx-industry"
                                className="input-base"
                                value={context.industry}
                                onChange={(e) =>
                                    setContext({ ...context, industry: e.target.value })
                                }
                                data-testid="ctx-industry-select"
                            >
                                <option value="">Select…</option>
                                {INDUSTRIES.map((i) => (
                                    <option key={i} value={i}>{i}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="label" htmlFor="ctx-users">Approximate user count<RequiredMark /></label>
                            <input
                                id="ctx-users"
                                type="number"
                                min={1}
                                className="input-base"
                                value={context.user_count}
                                onChange={(e) =>
                                    setContext({ ...context, user_count: e.target.value })
                                }
                                data-testid="ctx-users-input"
                            />
                        </div>
                        <div>
                            <label className="label" htmlFor="ctx-agreement">Current Microsoft agreement<RequiredMark /></label>
                            <select
                                id="ctx-agreement"
                                className="input-base"
                                value={context.agreement_type}
                                onChange={(e) =>
                                    setContext({ ...context, agreement_type: e.target.value })
                                }
                                data-testid="ctx-agreement-select"
                            >
                                <option value="">Select…</option>
                                {AGREEMENTS.map((a) => (
                                    <option key={a} value={a}>{a}</option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="label" htmlFor="ctx-licenses">
                                All Microsoft licenses in use (M365, Dynamics, Power Platform, Defender, Server, Copilot…)<RequiredMark />
                            </label>
                            <textarea
                                id="ctx-licenses"
                                rows={4}
                                className="input-base"
                                placeholder="e.g.\nM365 E3 (2,400 users), M365 F3 (1,100 users), Project Plan 3 (50), Visio Plan 2 (50)\nDynamics 365 Sales Enterprise (120 users), Dynamics 365 Customer Service (60 users)\nPower BI Pro (180), Power Apps Premium (40)\nDefender for Endpoint P2 (3,500), Defender for Office P2 (3,500)\nWindows 11 Enterprise E3, Windows Server 2022 Standard (24 cores)\nMicrosoft 365 Copilot (50 trial seats)"
                                value={context.license_types}
                                onChange={(e) =>
                                    setContext({ ...context, license_types: e.target.value })
                                }
                                data-testid="ctx-licenses-input"
                            />
                            <div className="text-xs text-[#9a9a9a] mt-2 leading-relaxed">
                                Include every Microsoft license family in use — not just Modern Work. You will be able to upload a license export later for richer detail.
                            </div>
                        </div>
                        <div className="md:col-span-2">
                            <label className="label" htmlFor="ctx-spend">
                                Current annual Microsoft spend (USD)
                            </label>
                            <input
                                id="ctx-spend"
                                type="number"
                                min={0}
                                step="0.01"
                                className="input-base"
                                placeholder="e.g. 1250000.00"
                                value={context.annual_spend}
                                onChange={(e) =>
                                    setContext({ ...context, annual_spend: e.target.value })
                                }
                                data-testid="ctx-spend-input"
                            />
                            <div className="text-xs text-[#9a9a9a] mt-2 leading-relaxed">
                                Enter <span className="text-white">0.00</span> if unknown or undisclosed. Estimated figures are accepted — the AI will treat them as such and downgrade the Licensing Risk &amp; Exposure Indicator&apos;s confidence accordingly.
                            </div>
                        </div>

                        {/* ----- Bucket A: engagement framing context (optional but recommended) ----- */}
                        <div className="md:col-span-2 mt-4 pt-6 border-t border-[#2a2a2a]">
                            <div className="overline mb-1">Engagement framing (optional)</div>
                            <p className="text-[13px] text-[#9a9a9a] leading-relaxed mb-4">
                                These tell the AI which narrative thread to use. Skip any you&apos;re unsure of; the report will still generate.
                            </p>
                        </div>
                        <div>
                            <label className="label" htmlFor="ctx-orgtype">Organisation type</label>
                            <select
                                id="ctx-orgtype"
                                className="input-base"
                                value={context.organisation_type}
                                onChange={(e) => setContext({ ...context, organisation_type: e.target.value })}
                                data-testid="ctx-orgtype-select"
                            >
                                <option value="">— Not specified —</option>
                                {ORG_TYPES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="label" htmlFor="ctx-goal">Primary engagement goal</label>
                            <select
                                id="ctx-goal"
                                className="input-base"
                                value={context.engagement_goal}
                                onChange={(e) => setContext({ ...context, engagement_goal: e.target.value })}
                                data-testid="ctx-goal-select"
                            >
                                <option value="">— Not specified —</option>
                                {ENGAGEMENT_GOALS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="label" htmlFor="ctx-ea">EA / agreement lifecycle stage</label>
                            <select
                                id="ctx-ea"
                                className="input-base"
                                value={context.ea_lifecycle_stage}
                                onChange={(e) => setContext({ ...context, ea_lifecycle_stage: e.target.value })}
                                data-testid="ctx-ea-select"
                            >
                                <option value="">— Not specified —</option>
                                {EA_LIFECYCLE.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="label">Microsoft products in scope (multi-select)</label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="ctx-products">
                                {PRODUCT_OPTIONS.map((p) => {
                                    const active = context.products_in_scope.includes(p);
                                    return (
                                        <button
                                            type="button"
                                            key={p}
                                            onClick={() => setContext({
                                                ...context,
                                                products_in_scope: active
                                                    ? context.products_in_scope.filter((x) => x !== p)
                                                    : [...context.products_in_scope, p],
                                            })}
                                            className={`text-left text-[13px] px-3 py-2 border transition-colors ${active ? "border-[#2ec5b6] bg-[#0f1a19] text-white" : "border-[#3a3a3a] bg-[#141414] text-[#c2c2c2] hover:border-[#6a6a6a]"}`}
                                            data-testid={`ctx-product-${p.split(" ")[0].toLowerCase()}`}
                                        >
                                            {active ? "✓ " : ""}{p}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="md:col-span-2">
                            <label className="label">Known / suspected pain points (multi-select)</label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="ctx-pain">
                                {PAIN_OPTIONS.map((p) => {
                                    const active = context.pain_points.includes(p);
                                    return (
                                        <button
                                            type="button"
                                            key={p}
                                            onClick={() => setContext({
                                                ...context,
                                                pain_points: active
                                                    ? context.pain_points.filter((x) => x !== p)
                                                    : [...context.pain_points, p],
                                            })}
                                            className={`text-left text-[13px] px-3 py-2 border transition-colors ${active ? "border-[#ff4d6d] bg-[#1f0f12] text-white" : "border-[#3a3a3a] bg-[#141414] text-[#c2c2c2] hover:border-[#6a6a6a]"}`}
                                        >
                                            {active ? "✓ " : ""}{p}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="text-xs text-[#9a9a9a] mt-2 leading-relaxed">
                                These are hypotheses, not findings. The AI will use evidence to corroborate or challenge them.
                            </div>
                        </div>

                        {/* --- JURISDICTION + SECURITY STACK --- */}
                        <div className="border border-[#2a2a2a] bg-[#141414] p-6 md:col-span-2">
                            <div className="overline mb-3">Jurisdiction & data-protection regime</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="ctx-country" className="text-[13px] text-[#9a9a9a] block mb-1">
                                        Country of operation
                                    </label>
                                    <select
                                        id="ctx-country"
                                        className="w-full bg-[#0a0a0a] border border-[#3a3a3a] text-white px-3 py-2 text-sm"
                                        data-testid="ctx-country"
                                        value={context.country}
                                        onChange={(e) => {
                                            const c = e.target.value;
                                            const match = (setupLists.countries || []).find((x) => x.name === c);
                                            setContext({
                                                ...context,
                                                country: c,
                                                data_protection_regime: match?.regime || context.data_protection_regime,
                                            });
                                        }}
                                    >
                                        <option value="">Select country…</option>
                                        {(setupLists.countries || []).map((c) => (
                                            <option key={c.name} value={c.name}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="ctx-regime" className="text-[13px] text-[#9a9a9a] block mb-1">
                                        Applicable data-protection regime <span className="text-[#7a7a7a]">(auto — overridable)</span>
                                    </label>
                                    <input
                                        id="ctx-regime"
                                        type="text"
                                        className="w-full bg-[#0a0a0a] border border-[#3a3a3a] text-white px-3 py-2 text-sm"
                                        placeholder="e.g. Data Protection Act 2020 (Jamaica)"
                                        data-testid="ctx-data-protection-regime"
                                        value={context.data_protection_regime}
                                        onChange={(e) => setContext({ ...context, data_protection_regime: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="text-xs text-[#9a9a9a] mt-2 leading-relaxed">
                                Drives the Security, Compliance & Identity Capability Alignment section of the report. Leave regime blank to auto-derive from the selected country.
                            </div>
                        </div>

                        <div className="border border-[#2a2a2a] bg-[#141414] p-6 md:col-span-2">
                            <div className="overline mb-3">Third-party security tools in use</div>
                            <div
                                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2"
                                data-testid="ctx-security-tools"
                            >
                                {(setupLists.security_tools || []).map((t) => {
                                    const active = context.security_tools.includes(t.name);
                                    return (
                                        <button
                                            key={t.name}
                                            type="button"
                                            onClick={() => setContext({
                                                ...context,
                                                security_tools: active
                                                    ? context.security_tools.filter((x) => x !== t.name)
                                                    : [...context.security_tools, t.name],
                                            })}
                                            data-testid={`sec-tool-${t.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                            className={`text-left text-[13px] px-3 py-2 border transition-colors ${active ? "border-[#2ec5b6] bg-[#0f1f1c] text-white" : "border-[#3a3a3a] bg-[#0a0a0a] text-[#c2c2c2] hover:border-[#6a6a6a]"}`}
                                        >
                                            <div className="font-mono text-[10px] text-[#7a7a7a] uppercase tracking-widest">
                                                {t.category}
                                            </div>
                                            <div>{active ? "✓ " : ""}{t.name}</div>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Custom "Other" tools that are NOT in the curated catalog. */}
                            <div className="mt-4 border-t border-[#2a2a2a] pt-4">
                                <div className="text-[13px] text-[#c2c2c2] mb-2">
                                    Other tools not listed above <span className="text-[#7a7a7a]">(comma-separated)</span>
                                </div>
                                <TagsInput
                                    className="w-full bg-[#0a0a0a] border border-[#3a3a3a] text-white px-3 py-2 text-sm"
                                    placeholder="e.g. Cyera, Wiz, Zscaler ZIA, custom in-house SIEM"
                                    data-testid="ctx-security-tools-other"
                                    value={context.security_tools.filter(
                                        (t) => !(setupLists.security_tools || []).some((cat) => cat.name === t)
                                    )}
                                    onChange={(others) => {
                                        const catalogNames = new Set(
                                            (setupLists.security_tools || []).map((t) => t.name)
                                        );
                                        const curated = context.security_tools.filter((t) => catalogNames.has(t));
                                        const dedupOthers = others.filter((s) => !catalogNames.has(s));
                                        setContext({
                                            ...context,
                                            security_tools: [...curated, ...dedupOthers],
                                        });
                                    }}
                                />
                                {context.security_tools.filter((t) => !(setupLists.security_tools || []).some((cat) => cat.name === t)).length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {context.security_tools
                                            .filter((t) => !(setupLists.security_tools || []).some((cat) => cat.name === t))
                                            .map((t) => (
                                                <span
                                                    key={`other-${t}`}
                                                    className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#2ec5b6] bg-[#0f1f1c] text-[11px] text-white"
                                                    data-testid={`sec-tool-other-chip-${t.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                                >
                                                    ✓ {t}
                                                </span>
                                            ))}
                                    </div>
                                )}
                            </div>

                            <div className="text-xs text-[#9a9a9a] mt-3 leading-relaxed">
                                Select all that apply. The report&apos;s Security, Compliance & Identity Capability Alignment section will assess coverage gaps between this stack and the data-protection regime above, and consider Microsoft E3 alongside leading 3rd-party alternatives.
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* CATEGORY QUESTIONS */}
            {currentCategory && (
                <section className="fade-up" data-testid={`step-${currentCategory}`}>
                    <div className="overline mb-2">
                        Category {String(step).padStart(2, "0")} / {String(categoryOrder.length).padStart(2, "0")}
                    </div>
                    <h2 className="font-display text-3xl md:text-5xl leading-tight">
                        {categories[currentCategory]}
                    </h2>
                    <div className="mt-10 space-y-8">
                        {(grouped[currentCategory] || []).map((q, idx) => (
                            <div
                                key={q.id}
                                className="border border-[#2a2a2a] bg-[#141414] p-6"
                                data-testid={`question-${q.id}`}
                            >
                                <div className="font-mono text-[13px] text-[#9a9a9a] mb-2">
                                    Q{String(idx + 1).padStart(2, "0")}
                                </div>
                                <div className="text-base md:text-lg text-white leading-relaxed">
                                    {q.text}
                                </div>
                                <div className="mt-5 grid grid-cols-5 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                                    {SCALE.map((s) => {
                                        const active = answers[q.id] === s.value;
                                        return (
                                            <button
                                                type="button"
                                                key={s.value}
                                                onClick={() => setAnswer(q.id, s.value)}
                                                className={`p-3 text-xs uppercase tracking-wider transition-colors ${
                                                    active
                                                        ? "bg-[#00A896] text-white"
                                                        : "bg-[#0a0a0a] text-[#c2c2c2] hover:bg-[#1f1f1f] hover:text-white"
                                                }`}
                                                data-testid={`answer-${q.id}-${s.value}`}
                                            >
                                                <div className="font-mono text-base text-white">
                                                    {s.value}
                                                </div>
                                                <div className="mt-1">{s.label}</div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* EVIDENCE STEP */}
            {isEvidence && (
                <section className="fade-up" data-testid="step-evidence">
                    <div className="overline mb-2">Evidence upload</div>
                    <h2 className="font-display text-3xl md:text-5xl leading-tight">
                        Upload your Microsoft licenses, user counts and infrastructure.
                    </h2>
                    <p className="text-[#c2c2c2] mt-4 max-w-3xl leading-relaxed text-[15px]">
                        The AI is constrained to facts present in (a) <strong className="text-white">what you upload here</strong> and (b) the admin-managed reference library. It will not invent SKU details. The richer your uploads, the sharper the report.
                    </p>

                    {/* Inherited evidence (re-run only) */}
                    {inheritedEvidence.length > 0 && (
                        <div
                            className="mt-6 border border-[#2ec5b6]/30 bg-[#0a1413] p-4"
                            data-testid="inherited-evidence-panel"
                        >
                            <div className="overline mb-2">
                                Carried over from previous version
                            </div>
                            <p className="text-[12px] text-[#c2c2c2] mb-3">
                                These files will be re-used as-is. Add new files below if you
                                have updated exports.
                            </p>
                            <ul className="space-y-1.5">
                                {inheritedEvidence.map((ie) => (
                                    <li
                                        key={ie.id}
                                        className="text-[13px] text-[#d4d4d4] flex items-center gap-2 flex-wrap"
                                        data-testid={`inherited-evidence-${ie.id}`}
                                    >
                                        <DoneIcon size={12} weight="bold" className="text-[#2ec5b6]" />
                                        <span className="font-mono truncate">{ie.filename}</span>
                                        <span className="px-1.5 py-0.5 border border-[#2a2a2a] text-[10px] uppercase tracking-widest text-[#c2c2c2]">
                                            {(ie.document_type || "other").replace("_", " ")}
                                        </span>
                                        <span className="text-[#7a7a7a] text-[11px]">
                                            {ie.kind}
                                            {ie.is_license_export ? " · license export" : ""}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => removeInheritedEvidence(ie)}
                                            className="ml-auto inline-flex items-center gap-1 text-[#ff4d6d] hover:text-[#ff8095] disabled:opacity-50 text-[11px] uppercase tracking-widest"
                                            disabled={removingInheritedId === ie.id}
                                            data-testid={`inherited-evidence-remove-${ie.id}`}
                                            title="Remove this carried-over file"
                                        >
                                            <Trash size={12} />
                                            {removingInheritedId === ie.id ? "Removing…" : "Remove"}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* PRIMARY zone — strongly prompted */}
                    <div className="mt-8 border border-[#2ec5b6]/40 bg-[#0f1a19] p-6">
                        <div className="overline mb-2">Highly recommended</div>
                        <h3 className="font-display text-xl text-white mb-3">
                            License inventory &amp; environment
                        </h3>
                        <ul className="text-[14px] text-[#d4d4d4] leading-relaxed mb-4 space-y-1.5">
                            <li>• <strong>Microsoft 365 admin centre license export</strong> (CSV / XLSX) — the single most valuable input</li>
                            <li>• Other license inventories: Dynamics 365, Power Platform, Defender, Project, Visio, Windows, Server, Copilot</li>
                            <li>• Total user count by persona (knowledge worker / frontline / executive / developer)</li>
                            <li>• Infrastructure count (servers, VMs, hybrid workloads, AWS-hosted Microsoft workloads)</li>
                            <li>• Current Enterprise Agreement / MCA-E / CSP / Open summary</li>
                        </ul>
                        <label className="inline-flex items-center gap-3 cursor-pointer border border-[#2ec5b6] bg-[#0a0a0a] hover:bg-[#1f1f1f] px-6 py-4 transition-colors">
                            <UploadSimple size={18} weight="bold" className="text-[#2ec5b6]" />
                            <span className="text-white font-semibold">
                                Upload license &amp; environment files
                            </span>
                            <input
                                type="file"
                                multiple
                                className="hidden"
                                accept=".pdf,.docx,.xlsx,.csv,.txt,.md,.log"
                                onChange={(e) => onPickFiles(e.target.files, "license_export")}
                                data-testid="evidence-picker"
                            />
                        </label>
                    </div>

                    {/* SECONDARY zone — typed supplementary documents */}
                    <div className="mt-6 border border-dashed border-[#3a3a3a] bg-[#0a0a0a] p-5" data-testid="evidence-supplementary-zone">
                        <div className="text-[13px] uppercase tracking-[0.12em] text-[#9a9a9a] font-semibold mb-1">
                            Other supporting documents (optional, tagged)
                        </div>
                        <p className="text-[13px] text-[#c2c2c2] leading-relaxed mb-4">
                            Tag each document by what it is. The AI treats each type
                            differently — strategy docs as ASSUMPTION, policies and client
                            governance minutes as FACT (with date citations), your own
                            consultation notes as ASSUMPTION until corroborated.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {[
                                {
                                    type: "tenant_connected_report",
                                    title: "Tenant-connected report",
                                    sub: "Actual export from the client's Microsoft 365 / Azure / Entra tenant",
                                    testid: "upload-tenant-connected",
                                },
                                {
                                    type: "strategy",
                                    title: "Strategy document",
                                    sub: "AI roadmap, IT strategy, board pre-read",
                                    testid: "upload-strategy",
                                },
                                {
                                    type: "policy",
                                    title: "Policy document",
                                    sub: "Acceptable use, data classification, vendor mgmt",
                                    testid: "upload-policy",
                                },
                                {
                                    type: "client_minutes",
                                    title: "Client governance minutes",
                                    sub: "IT Steering, Procurement Committee, Board",
                                    testid: "upload-client-minutes",
                                },
                                {
                                    type: "consultation_notes",
                                    title: "Your consultation notes",
                                    sub: "Your interview / discovery session notes",
                                    testid: "upload-consultation-notes",
                                },
                                {
                                    type: "other",
                                    title: "Other supporting document",
                                    sub: "EA/CSP summaries, invoices, org charts, etc.",
                                    testid: "upload-other",
                                },
                            ].map((opt) => (
                                <label
                                    key={opt.type}
                                    className="inline-flex items-center gap-3 cursor-pointer border border-[#2a2a2a] hover:border-[#2ec5b6]/60 bg-[#141414] hover:bg-[#1a1a1a] px-4 py-3 transition-colors"
                                    data-testid={opt.testid}
                                >
                                    <UploadSimple size={14} weight="bold" className="text-[#2ec5b6]" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-white text-[14px] truncate">
                                            {opt.title}
                                        </div>
                                        <div className="text-[11px] text-[#9a9a9a] truncate">
                                            {opt.sub}
                                        </div>
                                    </div>
                                    <input
                                        type="file"
                                        multiple
                                        className="hidden"
                                        accept=".pdf,.xlsx,.csv,.txt,.md,.log,.docx"
                                        onChange={(e) => onPickFiles(e.target.files, opt.type)}
                                    />
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="mt-4 text-[12px] text-[#9a9a9a]">
                        Accepted: PDF, DOCX, XLSX, CSV, TXT, MD. Max 8 files × 25 MB each.
                    </div>

                    {evidence.length > 0 && (
                        <div className="mt-6 border border-[#2a2a2a] divide-y divide-[#2a2a2a]">
                            {evidence.map((e, i) => (
                                <div
                                    key={`ev-${i}`}
                                    className="bg-[#141414] p-4 grid grid-cols-12 gap-3 items-center"
                                    data-testid={`evidence-row-${i}`}
                                >
                                    <div className="col-span-12 md:col-span-7 flex items-center gap-3">
                                        <FileIcon size={18} className="text-[#9a9a9a]" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-white truncate">{e.file.name}</div>
                                            <div className="text-xs text-[#9a9a9a] flex items-center gap-2 flex-wrap">
                                                <span>
                                                    {(e.file.size / 1024).toFixed(0)} KB
                                                    {e.chars ? ` · ${e.chars.toLocaleString()} chars` : ""}
                                                </span>
                                                <span className="px-1.5 py-0.5 border border-[#2a2a2a] text-[10px] uppercase tracking-widest text-[#c2c2c2]">
                                                    {(e.documentType || "other").replace("_", " ")}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-span-8 md:col-span-3 text-sm">
                                        {e.status === "pending" && (
                                            <span className="text-[#9a9a9a]">Pending</span>
                                        )}
                                        {e.status === "uploading" && (
                                            <span className="text-[#2ec5b6]">Uploading…</span>
                                        )}
                                        {e.status === "done" && (
                                            <span className="inline-flex items-center gap-1 text-[#2ec5b6]">
                                                <DoneIcon size={14} weight="bold" /> Extracted
                                            </span>
                                        )}
                                        {e.status === "failed" && (
                                            <span className="inline-flex items-center gap-1 text-[#ff4d6d]" title={e.error}>
                                                <Warning size={14} /> {e.error || "Failed"}
                                            </span>
                                        )}
                                    </div>
                                    <div className="col-span-4 md:col-span-2 flex justify-end">
                                        {e.status !== "done" && (
                                            <button
                                                className="btn-ghost"
                                                onClick={() => removeEvidence(i)}
                                                disabled={evidenceUploading}
                                                data-testid={`evidence-remove-${i}`}
                                            >
                                                <Trash size={14} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="mt-6 text-sm text-[#9a9a9a]">
                        Confidence preview: {" "}
                        <span className="text-white font-semibold">
                            {evidence.length === 0
                                ? "Low (self-assessment only)"
                                : evidence.length >= 3 || Number(context.annual_spend) > 0
                                  ? "Medium / High"
                                  : "Medium"}
                        </span>
                        {" "} — finalised after upload (license-inventory upload + spend disclosure → High).
                    </div>
                </section>
            )}

            {/* REVIEW */}
            {isReview && (
                <section className="fade-up" data-testid="step-review">
                    <div className="overline mb-2">Review & generate</div>
                    <h2 className="font-display text-3xl md:text-5xl leading-tight">
                        Ready to generate your report
                    </h2>
                    <p className="text-[#c2c2c2] mt-4 max-w-2xl leading-relaxed">
                        We will compute Red / Amber / Green ratings, and produce a
                        full executive narrative. This typically takes 20–40
                        seconds.
                    </p>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                        {[
                            ["Organization", context.organization_name],
                            ["Industry", context.industry],
                            ["Users", context.user_count],
                            ["Agreement", context.agreement_type],
                            [
                                "Annual MS Spend (USD)",
                                Number(context.annual_spend) > 0
                                    ? Number(context.annual_spend).toLocaleString(undefined, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                      })
                                    : "Not provided",
                            ],
                            [
                                "Supporting documents",
                                evidence.length === 0
                                    ? "None"
                                    : `${evidence.length} file${evidence.length === 1 ? "" : "s"}`,
                            ],
                        ].map(([k, v]) => (
                            <div key={k} className="bg-[#0a0a0a] p-5">
                                <div className="font-mono text-[13px] text-[#9a9a9a] uppercase tracking-widest">
                                    {k}
                                </div>
                                <div className="mt-1 text-white">{v}</div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-6 border border-[#2a2a2a] bg-[#0a0a0a] p-5">
                        <div className="font-mono text-[13px] text-[#9a9a9a] uppercase tracking-widest">
                            License types
                        </div>
                        <div className="mt-1 text-[#d4d4d4] whitespace-pre-line">
                            {context.license_types}
                        </div>
                    </div>
                </section>
            )}

            {err && (
                <div className="block-risk mt-8 text-sm" data-testid="assessment-error">
                    {err}
                </div>
            )}

            {/* Nav */}
            <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-[#2a2a2a] pt-6">
                <button
                    type="button"
                    className="btn-secondary inline-flex items-center gap-2"
                    onClick={() => setStep(Math.max(0, step - 1))}
                    disabled={step === 0 || submitting}
                    data-testid="assessment-back-button"
                >
                    <ArrowLeft size={14} weight="bold" /> Back
                </button>
                {!isReview ? (
                    <button
                        type="button"
                        className="btn-primary inline-flex items-center gap-2"
                        onClick={() => setStep(step + 1)}
                        disabled={
                            (isContext && !contextValid) ||
                            (currentCategory && !currentCategoryComplete)
                        }
                        data-testid="assessment-next-button"
                    >
                        Continue <ArrowRight size={14} weight="bold" />
                    </button>
                ) : (
                    <button
                        type="button"
                        className="btn-primary inline-flex items-center gap-2"
                        onClick={submit}
                        disabled={submitting || !allAnswered || !contextValid}
                        data-testid="assessment-submit-button"
                    >
                        <CheckCircle size={16} weight="bold" />
                        {submitting ? "Generating report… (90–180s)" : "Generate executive report"}
                    </button>
                )}
            </div>
        </div>
    );
}
